import {
  createPaymentRequiredError,
  isPaymentRequiredError,
  isPaymentRequiredPayload,
} from './creditConsumption';

export const GENERATION_ERROR_MESSAGE =
  "Une erreur est survenue lors de la génération. Veuillez réessayer. Si le problème persiste, contactez notre support.";

export const GENERATION_STATUS = {
  PROCESSING: 'PROCESSING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

export const DEFAULT_GENERATION_POLL_INTERVAL_MS = 2500;
export const DEFAULT_GENERATION_POLL_TIMEOUT_MS = 20 * 60 * 1000;

export const CONTENT_TYPE_CONFIG = {
  PRODUCT: {
    service: 'PRODUCT',
    contentType: 'PRODUCT',
    agentType: 'PRODUCT',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  LONG_CATEGORY: {
    service: 'LONG_CATEGORY',
    contentType: 'LONG_CATEGORY',
    agentType: 'LONG_CATEGORY',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  STORE_LOCATOR: {
    service: 'STORE_LOCATOR',
    contentType: 'STORE_LOCATOR',
    agentType: 'STORE_LOCATOR',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  GMB: {
    service: 'GMB',
    contentType: 'GMB',
    agentType: 'GMB',
    showSeoControls: false,
    showUrlControls: true,
    showScore: false,
  },
  CONTENT_IMPROVEMENT: {
    service: 'CONTENT_IMPROVEMENT',
    contentType: 'CONTENT_IMPROVEMENT',
    agentType: 'CONTENT_IMPROVEMENT',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  OTHER: {
    service: 'OTHER',
    contentType: 'OTHER',
    agentType: 'OTHER',
    showSeoControls: true,
    showUrlControls: true,
    showScore: true,
  },
  YOUTUBE: {
    service: 'YOUTUBE',
    contentType: 'YOUTUBE',
    agentType: 'YOUTUBE',
    showSeoControls: false,
    showUrlControls: false,
    showScore: false,
  },
};

const DEFAULT_MODELS_BY_PROVIDER = {
  openai: 'GPT_4_1',
  claude: 'CLAUDE_SONNET_4_6',
  perplexity: 'SONAR',
  gemini: 'GEMINI_2_5_PRO',
};

export const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

export const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  if (id === 'gemini') return 'Gemini';
  if (id === 'premium') return 'premium';
  return String(id || '').toUpperCase();
};

export const getLlmIdFromAgentModel = (model) => {
  if (model == null || !String(model).trim()) return '';
  const modelLower = String(model).trim().toLowerCase();
  if (modelLower.includes('gpt') && modelLower.includes('claude')) return 'claude_gpt';
  if (modelLower.includes('premium')) return 'premium';
  if (modelLower.includes('perplexity')) return 'perplexity';
  if (modelLower.includes('gemini')) return 'gemini';
  if (modelLower.includes('claude')) return 'claude';
  if (modelLower.includes('gpt')) return 'gpt';
  return '';
};

const ENRICHMENT_ALLOWED_LLMS = new Set(['claude', 'gemini']);

export const getAgentSettingsFlagsFromLlm = (llm, { isContentImprovement = false } = {}) => {
  let normalized = normalizeLlmId(llm) || (isContentImprovement ? 'claude' : 'gpt');
  if (isContentImprovement && !ENRICHMENT_ALLOWED_LLMS.has(normalized)) {
    normalized = 'claude';
  }
  return {
    useGpt: normalized === 'gpt',
    useClaude: normalized === 'claude',
    useBoth: normalized === 'claude_gpt',
    usePerplexity: normalized === 'perplexity',
    useGemini: normalized === 'gemini',
    usePremium: normalized === 'premium',
  };
};

export const resolveContentTypeKey = (order, projectSummary) => {
  const contentType = String(order?.contentType || '').toUpperCase();
  if (contentType === 'OPTIMIZE') return 'CONTENT_IMPROVEMENT';

  const agentType = String(
    projectSummary?.projectAiAgent?.typeContenu ||
    projectSummary?.projectAiAgent?.contentType ||
    ''
  ).toUpperCase();
  if (agentType && CONTENT_TYPE_CONFIG[agentType]) return agentType;

  return 'PRODUCT';
};

export const YOUTUBE_API_ENDPOINT = '/smart-content/smart_content/youtube_description';

export const buildYoutubeApiUrl = () => {
  const params = new URLSearchParams();
  params.set('reasoning', 'LOW');
  params.set('activeWebSearch', 'true');
  return `${YOUTUBE_API_ENDPOINT}?${params.toString()}`;
};

export const resolveProjectLlm = (order, redactionData, projectSummary) => {
  const llmSource =
    order?.llmType ||
    projectSummary?.projectLLMType ||
    redactionData?.aiAgentModel ||
    '';
  return getLlmIdFromAgentModel(llmSource) || normalizeLlmId(llmSource) || 'gpt';
};

export const getTextFromHtml = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html || '';
  return div.textContent || div.innerText || '';
};

export const plainTextToHtml = (text) => {
  const trimmed = String(text || '').trim();
  if (!trimmed) return '';
  if (trimmed.includes('<')) return trimmed;
  return `<p>${trimmed.replace(/\n\n/g, '</p><p>').replace(/\n/g, '<br>')}</p>`;
};

export const formatGeneratedTextToHtml = (generatedText) => {
  if (!generatedText || generatedText.trim() === '') return '';
  if (generatedText.includes('<')) return generatedText;
  return `<p>${generatedText.replace(/\n/g, '</p><p>')}</p>`;
};

export const isGenerationLoadingContent = (html) => {
  if (!html || typeof html !== 'string') return false;
  return html.includes('gifencours.gif') || html.includes('Rédaction en cours');
};

const getUserIaConfigsFromLocalStorage = () => {
  try {
    const rawUser = localStorage.getItem('user');
    if (!rawUser) return [];
    const parsed = JSON.parse(rawUser);
    const iaConfigs = parsed?.user?.iaConfigs || parsed?.iaConfigs;
    return Array.isArray(iaConfigs) ? iaConfigs : [];
  } catch {
    return [];
  }
};

export const getActiveTokenForProvider = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  if (!providerKey) return '';

  const iaConfigs = getUserIaConfigsFromLocalStorage();
  const item = iaConfigs.find(
    (c) =>
      c?.active === true &&
      String(c?.iaProvider || '').toLowerCase() === providerKey &&
      typeof c?.token === 'string'
  );

  return item?.token?.trim?.() || '';
};

const normalizeModelForApi = (model) => {
  if (!model) return '';
  return String(model)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();
};

const getActiveIaConfigForProvider = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  if (!providerKey) return null;
  const iaConfigs = getUserIaConfigsFromLocalStorage();
  return (
    iaConfigs.find(
      (c) =>
        c?.active === true &&
        String(c?.iaProvider || '').toLowerCase() === providerKey
    ) || null
  );
};

const getModelForProviderInApi = (iaProvider) => {
  const providerKey = String(iaProvider || '').toLowerCase();
  const activeConfig = getActiveIaConfigForProvider(providerKey);
  const activeModel = normalizeModelForApi(activeConfig?.iaModel);
  if (activeModel) return activeModel;
  if (providerKey === 'openai') {
    return DEFAULT_MODELS_BY_PROVIDER.openai;
  }
  return DEFAULT_MODELS_BY_PROVIDER[providerKey] || '';
};

export const buildIaHeadersForTitleMeta = (selectedLlm) => {
  const llm = String(selectedLlm || '').toLowerCase();
  const headers = {};

  if (llm === 'claude') {
    const token = getActiveTokenForProvider('claude');
    if (token) headers['X-Anthropic-Api-Key'] = token;
  } else {
    const token = getActiveTokenForProvider('openai');
    if (token) headers['X-OpenAI-Api-Key'] = token;
  }

  return headers;
};

export const getTitleAndMetaUrls = (kw, website, language, llm) => {
  const encodedKw = encodeURIComponent(kw || '');
  const encodedWebsite = encodeURIComponent(website || '');
  const encodedLanguage = encodeURIComponent(language || '');
  const selectedLlm = String(llm || 'gpt').toLowerCase();

  const openAiModel = getModelForProviderInApi('openai');
  const claudeModel = getModelForProviderInApi('claude');
  if (selectedLlm === 'claude') {
    return {
      titleUrl: `/smart-content/smart_content/generate_title/claude?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&claudeModel=${encodeURIComponent(claudeModel)}&temperature=0.3`,
      metaUrl: `/smart-content/smart_content/generate_meta_description/claude?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&claudeModel=${encodeURIComponent(claudeModel)}&temperature=0.3`,
    };
  }
  return {
    titleUrl: `/smart-content/smart_content/generate_title/gpt?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&chatGPTModel=${encodeURIComponent(openAiModel)}&temperature=0.3`,
    metaUrl: `/smart-content/smart_content/generate_meta_description/gpt?keyword=${encodedKw}&website=${encodedWebsite}&langue=${encodedLanguage}&maxTokens=4000&chatGPTModel=${encodeURIComponent(openAiModel)}&temperature=0.3`,
  };
};

export const extractField = (res, candidates = []) => {
  try {
    const d = res?.data;
    if (typeof d === 'string') return d;
    for (const key of candidates) {
      if (d?.[key]) return d[key];
    }
    if (d?.data) {
      if (typeof d.data === 'string') return d.data;
      for (const key of candidates) {
        if (d.data?.[key]) return d.data[key];
      }
    }
  } catch {
    // ignore
  }
  return '';
};

export const llmIdToApiKey = (llmId) => {
  const normalized = normalizeLlmId(llmId);
  if (normalized === 'claude_gpt') return 'both';
  return normalized || 'gpt';
};

export const llmIdToGenerationApiValue = (llmId) => {
  const normalized = normalizeLlmId(llmId) || 'gpt';
  if (normalized === 'claude_gpt' || normalized === 'both') return 'CLAUDE_GPT';
  if (normalized === 'claude') return 'CLAUDE';
  if (normalized === 'gemini') return 'GEMINI';
  if (normalized === 'perplexity') return 'PERPLEXITY';
  if (normalized === 'premium') return 'PREMIUM';
  return 'GPT';
};

export const resolveTypeGeneration = (serviceOrIsEnrichment) => {
  if (serviceOrIsEnrichment === true) return 'OPTIMIZATION';
  const service = String(serviceOrIsEnrichment || '').toUpperCase();
  if (
    service === 'CONTENT_IMPROVEMENT' ||
    service === 'ENRICHISSEMENT' ||
    service === 'OPTIMIZATION'
  ) {
    return 'OPTIMIZATION';
  }
  return 'REDACTION';
};

export const buildUnassignedGenerationUrl = (websiteId) =>
  `/texts/external/unassigned/generation/${websiteId}`;

export const buildUnassignedRegenerationUrl = (textId, websiteId) =>
  `/texts/external/unassigned/generation/${textId}/website/${websiteId}`;

export const buildUnassignedGenerationStatusUrl = (
  websiteId,
  { textId, website, typeGeneration } = {}
) => {
  const params = new URLSearchParams();
  if (textId != null && String(textId).trim() !== '') {
    params.set('textId', String(textId).trim());
  }
  if (website) params.set('website', website);
  if (typeGeneration) params.set('typeGeneration', typeGeneration);
  const query = params.toString();
  return `${buildUnassignedGenerationUrl(websiteId)}/status${query ? `?${query}` : ''}`;
};

export const buildUnassignedGenerationPayload = ({
  llm,
  llmType,
  agentId,
  axeRedaction,
  gap,
  keyword,
  maillageInterne,
  nombreMots,
  service,
  urlPage,
  website,
  typeGeneration,
  contentType,
} = {}) => ({
  llmType: llmType || llmIdToGenerationApiValue(llm),
  agentId: agentId || '',
  axeRedaction: axeRedaction || '',
  gap: Array.isArray(gap) ? gap : [],
  keyword: keyword || '',
  maillageInterne: Array.isArray(maillageInterne) ? maillageInterne : [],
  nombreMots: String(nombreMots ?? 0),
  service: service || '',
  urlPage: urlPage || '',
  website: website || '',
  typeGeneration: typeGeneration || 'REDACTION',
  contentType: contentType || '',
});

export const buildUnassignedGenerationFormData = (data, files = []) => {
  const formData = new FormData();
  formData.append(
    'data',
    new Blob([JSON.stringify(data || {})], { type: 'application/json' })
  );
  (Array.isArray(files) ? files : []).forEach((file) => {
    if (file) formData.append('files', file);
  });
  return formData;
};

export const getMultipartRequestConfig = (extraHeaders = {}) => ({
  headers: {
    'Content-Type': false,
    ...extraHeaders,
  },
});

const normalizeStatusValue = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');

/** Status API: [{ textId, jobStatus }] */
export const unwrapGenerationStatusData = (data, { textId } = {}) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const jobs = data.filter(
    (job) =>
      job &&
      job.textId != null &&
      String(job.textId).trim() !== '' &&
      job.jobStatus != null &&
      String(job.jobStatus).trim() !== ''
  );
  if (jobs.length === 0) return null;

  if (textId != null && String(textId).trim() !== '') {
    const byId = jobs.find((job) => String(job.textId) === String(textId));
    if (byId) return byId;
  }

  return (
    jobs.find((job) => isGenerationCompleted(job.jobStatus)) ||
    jobs.find((job) => isGenerationFailed(job.jobStatus)) ||
    jobs[0]
  );
};

export const extractGenerationStatus = (data, { textId } = {}) => {
  const job = unwrapGenerationStatusData(data, { textId });
  return job ? normalizeStatusValue(job.jobStatus) : '';
};

export const isGenerationInProgress = (status) =>
  normalizeStatusValue(status) === GENERATION_STATUS.PROCESSING;

export const isGenerationCompleted = (status) =>
  normalizeStatusValue(status) === GENERATION_STATUS.COMPLETED;

export const isGenerationFailed = (status) =>
  normalizeStatusValue(status) === GENERATION_STATUS.FAILED;

/** Content from fullDetails */
export const extractGeneratedContent = (data) => {
  if (!data || typeof data !== 'object') return '';
  const html = data.actualHtmlContent || data.htmlContent || '';
  if (typeof html === 'string' && html.trim()) return html;
  const text = data.actualTextContent || '';
  return typeof text === 'string' ? text : '';
};

const loadGeneratedHtmlByTextId = async (httpClient, textId) => {
  if (!httpClient || !textId) return '';
  const detailsResponse = await httpClient.get(
    `/texts/external/unassigned/${textId}/fullDetails`
  );
  const content = extractGeneratedContent(detailsResponse?.data);
  return content ? formatGeneratedTextToHtml(content) : '';
};


export const runUnassignedGenerationJob = async ({
  httpClient,
  websiteId,
  payload,
  files = [],
  website,
  typeGeneration = 'REDACTION',
  textId: initialTextId = null,
  isRelaunch = false,
  pollIntervalMs = DEFAULT_GENERATION_POLL_INTERVAL_MS,
  pollTimeoutMs = DEFAULT_GENERATION_POLL_TIMEOUT_MS,
  onJobAccepted,
  signal,
} = {}) => {
  if (!httpClient || !websiteId) {
    throw new Error('websiteId manquant pour la génération');
  }

  const effectiveTextId =
    initialTextId != null && String(initialTextId).trim() !== ''
      ? String(initialTextId).trim()
      : null;

  const multipartBody = buildUnassignedGenerationFormData(payload, files);
  const multipartConfig = getMultipartRequestConfig();

  let startResponse;
  try {
    if (isRelaunch) {
      if (!effectiveTextId) {
        throw new Error('textId manquant pour la relance de génération');
      }
      startResponse = await httpClient.put(
        buildUnassignedRegenerationUrl(effectiveTextId, websiteId),
        multipartBody,
        multipartConfig
      );
    } else {
      startResponse = await httpClient.post(
        buildUnassignedGenerationUrl(websiteId),
        multipartBody,
        multipartConfig
      );
    }
  } catch (error) {
    if (isPaymentRequiredError(error)) {
      throw createPaymentRequiredError(error?.response?.data, error.message);
    }
    throw error;
  }

  if (isPaymentRequiredPayload(startResponse?.data)) {
    throw createPaymentRequiredError(startResponse.data);
  }

  onJobAccepted?.(startResponse?.data);

  return watchUnassignedGenerationUntilDone({
    httpClient,
    websiteId,
    textId: effectiveTextId,
    website,
    typeGeneration,
    pollIntervalMs,
    pollTimeoutMs,
    signal,
  });
};

export const fetchUnassignedGenerationStatus = async ({
  httpClient,
  websiteId,
  textId,
  website,
  typeGeneration = 'REDACTION',
} = {}) => {
  if (!httpClient || !websiteId) {
    throw new Error('websiteId manquant pour le statut de génération');
  }

  const statusResponse = await httpClient.get(
    buildUnassignedGenerationStatusUrl(websiteId, {
      textId,
      website,
      typeGeneration,
    })
  );
  const statusData = statusResponse?.data;

  if (
    statusResponse?.status === 204 ||
    statusData == null ||
    statusData === '' ||
    (Array.isArray(statusData) && statusData.length === 0)
  ) {
    return {
      status: GENERATION_STATUS.COMPLETED,
      textId: textId || null,
    };
  }

  const statusJob = unwrapGenerationStatusData(statusData, { textId });
  return {
    status: statusJob ? normalizeStatusValue(statusJob.jobStatus) : '',
    textId: statusJob?.textId ? String(statusJob.textId) : textId || null,
  };
};

export const watchUnassignedGenerationUntilDone = async ({
  httpClient,
  websiteId,
  textId: initialTextId = null,
  website,
  typeGeneration = 'REDACTION',
  pollIntervalMs = DEFAULT_GENERATION_POLL_INTERVAL_MS,
  pollTimeoutMs = DEFAULT_GENERATION_POLL_TIMEOUT_MS,
  signal,
} = {}) => {
  if (!httpClient || !websiteId) {
    throw new Error('websiteId manquant pour le suivi de génération');
  }

  let textId =
    initialTextId != null && String(initialTextId).trim() !== ''
      ? String(initialTextId).trim()
      : null;
  const startedAt = Date.now();

  while (Date.now() - startedAt < pollTimeoutMs) {
    if (signal?.aborted) {
      throw new Error('Suivi de génération annulé');
    }

    const { status, textId: nextTextId } = await fetchUnassignedGenerationStatus({
      httpClient,
      websiteId,
      textId,
      website,
      typeGeneration,
    });

    if (nextTextId) textId = String(nextTextId);

    if (isGenerationFailed(status)) {
      throw new Error(GENERATION_ERROR_MESSAGE);
    }

    if (isGenerationCompleted(status)) {
      let html = '';
      try {
        html = await loadGeneratedHtmlByTextId(httpClient, textId);
      } catch {
        // ignore
      }
      return {
        html,
        textId,
        completedWithoutContent: !html,
      };
    }

    await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
  }

  throw new Error('Délai dépassé lors de la génération. Veuillez réessayer.');
};

export const getPresetForWords = (words) => {
  const n = Number(words) || 0;
  if (n === 300) return { preset: 'ARTICLE_COURT_300', presetWordCount: '' };
  if (n === 600) return { preset: 'ARTICLE_MOYEN_600', presetWordCount: '' };
  if (n === 1000) return { preset: 'ARTICLE_LONG_1000', presetWordCount: '' };
  if (n === 350) return { preset: 'YOUTUBE_350', presetWordCount: '' };
  if (n === 200) return { preset: 'LINKEDIN_200', presetWordCount: '' };
  if (n === 150) return { preset: 'X_150', presetWordCount: '' };
  if (!n) return { preset: '', presetWordCount: '' };
  return { preset: 'CUSTOM', presetWordCount: String(n) };
};

export const wordsFromPresetForm = (form) => {
  let words = form.presetWordCount ? Number(form.presetWordCount) : 0;
  if (!words && form.preset) {
    const presetMap = {
      ARTICLE_COURT_300: 300,
      ARTICLE_MOYEN_600: 600,
      ARTICLE_LONG_1000: 1000,
      YOUTUBE_350: 350,
      LINKEDIN_200: 200,
      X_150: 150,
    };
    words = presetMap[form.preset] || 0;
  }
  return words;
};
